import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent, EmptyState, ErrorBanner } from '@saas-kit/ui';
import type { PaginationState } from '@saas-kit/ui/DataTable';
import { AuditTable, listAuditLogs } from '../audit';

const DEFAULT_PAGE_SIZE = 25;

interface Props {
  tenantId: string;
}

export function TenantDetailAudit({ tenantId }: Props): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const logsQuery = useQuery({
    queryKey: ['admin-tenant-audit', tenantId, { offset, limit: pageSize }],
    queryFn: () =>
      listAuditLogs({ tenant_id: tenantId, offset, limit: pageSize }),
    placeholderData: (prev) => prev,
  });

  const logs = logsQuery.data?.items ?? [];
  const page = logsQuery.data?.page;
  const total = page?.total ?? 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty = !logsQuery.isLoading && !logsQuery.isError && total === 0;

  if (logsQuery.isError) {
    return <ErrorBanner>{t('detail.audit.error')}</ErrorBanner>;
  }

  if (showEmpty) {
    return (
      <Card>
        <CardContent className="p-0">
          <EmptyState
            title={t('detail.audit.empty_title')}
            description={t('detail.audit.empty_body')}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <AuditTable
      logs={logs}
      pagination={pagination}
      isLoading={logsQuery.isLoading}
      error={logsQuery.isError ? t('detail.audit.error') : null}
    />
  );
}
